/**
 * Matter-requirement document-attach client. Backs the redemption "Attach from
 * Smokeball" (PMS import) and "Choose existing document" flows via the unified
 * source-discriminated endpoint (ADOC-4, LF-6190):
 * `POST /matters/:matterId/documents/attach`. The from-computer upload keeps its
 * own two-step presigned flow (`useFileUpload`) and is not routed here. Backed by
 * `directApiClient` (LF-3017 preferred pattern); the backend lands the file as the
 * requested `fileType` with the same associations the upload-from-machine handler
 * passes, so the end state matches a manual upload.
 */

'use client';

import { directApiClient } from '@/lib/api/direct-client';

/**
 * The types a document can be attached as. The four Prepare-sheet requirement
 * types land against a requirement; `additional-document` is a neutral matter
 * document (no requirement association) — the landing the PMS import uses for the
 * email/thread attach surfaces, which reference the returned fileId (ADOC-7,
 * LF-6245). `distribution-declaration` is the beneficiary declaration signed on
 * paper and filed back (VALE-895); like `additional-document` it carries no
 * requirement association.
 *
 * **This mirrors the backend's `PMS_IMPORTABLE_FILE_TYPES`** and is the wider of
 * the two sets the attach endpoint checks: `source: 'existing'` is validated
 * against the narrower `REQUIREMENT_DOCUMENT_FILE_TYPES`, so
 * `additional-document` and `distribution-declaration` are importable from the
 * PMS but not linkable from an existing matter document. A surface offering
 * choose-existing on either of those will 400.
 *
 * `will` and `death-certificate` joined the list in VALE-1154, which widened
 * `REQUIREMENT_DOCUMENT_FILE_TYPES` so those two core slots could take a
 * non-upload source at all. Until then a lawyer could file a will from their
 * own machine but not from an existing matter document and not from the firm's
 * PMS. This union is a hand-kept mirror of a backend constant it cannot import;
 * when that constant moves, this moves in the same change.
 */
export type PmsImportFileType =
  | 'will'
  | 'death-certificate'
  | 'grant-of-representation'
  | 'primary-id'
  | 'institution-form-signed'
  | 'correspondence-attachment'
  | 'additional-document'
  | 'distribution-declaration';

/** Which source the unified attach endpoint lands a document from. */
export type AttachMatterDocumentSource = 'pms' | 'existing';

/** Shared association block, mirroring what the matching upload-from-machine handler passes. */
interface RequirementAssociations {
  /** What to attach the document as. */
  fileType: PmsImportFileType;
  /** Contact whose ID this is (primary-id). */
  contactId?: number;
  /** Institution the form is for (institution-form-signed). */
  institutionId?: number;
  /** Institution form template id (institution-form-signed; omit for a free-form). */
  institutionFormId?: number;
  /** Contact who signed the form (institution-form-signed). */
  signatoryContactId?: number;
  /** Inventory item the form is for (inventory-item-specific institution forms). */
  inventoryItemId?: number;
  /** Correspondence id to attach to (correspondence-attachment). */
  attachmentId?: number;
}

export interface AttachMatterDocumentRequest extends RequirementAssociations {
  /** Which source to land the document from. */
  source: AttachMatterDocumentSource;
  /** Vale file id of the existing matter document to copy — required when source='existing'. */
  fileId?: number;
  /** Identifier of the file in the PMS — required when source='pms'. */
  externalId?: string;
  /** Original file name in the PMS — required when source='pms'. */
  fileName?: string;
}

export interface AttachMatterDocumentResponse {
  fileId: number;
  source: AttachMatterDocumentSource;
  fileName: string;
  /** Source Vale file id (source='existing' only). */
  sourceFileId?: number;
  /** PMS external id (source='pms' only). */
  externalId?: string;
}

/**
 * Attach a document to a Prepare-sheet requirement via the unified endpoint —
 * from the firm's PMS (`source: 'pms'`, with `externalId` + `fileName`) or
 * by copying an existing matter document (`source: 'existing'`, with `fileId`).
 * The backend lands it as `fileType` with the same associations, so the end state
 * matches a manual upload. Throws on failure (surfaced by the caller's try/catch),
 * mirroring the imperative upload path.
 */
export async function attachMatterDocument(
  matterId: number,
  request: AttachMatterDocumentRequest
): Promise<AttachMatterDocumentResponse> {
  return directApiClient.post<
    AttachMatterDocumentResponse,
    AttachMatterDocumentRequest
  >(`/matters/${matterId}/documents/attach`, request);
}
